import { Link } from 'react-router-dom'
import styled, { createGlobalStyle, css } from 'styled-components'

const t = (path, fallback) => (props) => {
  const segments = path.split('.')
  let value = props.theme
  for (const segment of segments) {
    if (value == null) return fallback
    value = value[segment]
  }
  return value ?? fallback
}

export const PatientGlobalStyle = createGlobalStyle`
  body[data-portal='patient'] {
    background: ${t('color.bg', '#FAF7F2')};
    color: ${t('color.ink', '#1F2937')};
    font-family: ${t('type.family', "'Outfit', sans-serif")};
  }

  .patient-portal-root *::-webkit-scrollbar { width: 10px; height: 10px; }
  .patient-portal-root *::-webkit-scrollbar-thumb {
    background: ${t('color.border', '#E6E0D6')};
    border-radius: 999px;
  }

  /* A11y: visible focus rings on every interactive element inside the patient
     portal. We keep the existing visual but ensure keyboard users always see
     where they are. */
  .patient-portal-root :focus-visible,
  .patient-portal-root button:focus-visible,
  .patient-portal-root a:focus-visible,
  .patient-portal-root input:focus-visible,
  .patient-portal-root textarea:focus-visible,
  .patient-portal-root select:focus-visible {
    outline: 2px solid ${t('color.primary', '#1F3A5F')};
    outline-offset: 2px;
    border-radius: 6px;
  }

  /* Respect prefers-reduced-motion across the portal. The pulse on
     NextSessionCard, the shimmer on skeletons, and the conic-gradient
     transitions on PackProgressCard all stop animating. */
  @media (prefers-reduced-motion: reduce) {
    .patient-portal-root *,
    .patient-portal-root *::before,
    .patient-portal-root *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }

  /* Print stylesheet — invoked when the patient prints any portal page.
     Hides the chrome (top bar, left rail, bottom nav, dialogs) and renders
     the canvas as a clean A4-friendly document. Prescription / receipt /
     notes pages already render print-ready content; this just clears
     the surrounding shell. */
  @media print {
    body[data-portal='patient'] {
      background: white !important;
      color: black !important;
    }
    /* Hide portal chrome */
    .patient-portal-root header,
    .patient-portal-root aside,
    .patient-portal-root nav,
    .patient-portal-root .patient-topbar-menu-btn,
    [role='dialog'],
    [role='listbox'] {
      display: none !important;
    }
    /* Expand the canvas to full page width */
    .patient-portal-root main {
      padding: 0 !important;
      max-width: 100% !important;
    }
    /* Clinical authority: serif body for printed copies */
    .patient-portal-root main * {
      font-family: 'Georgia', 'Times New Roman', serif !important;
      box-shadow: none !important;
    }
    /* Cards become clean borders rather than the cream background */
    .patient-portal-root main section,
    .patient-portal-root main article {
      border: 1px solid #999 !important;
      background: white !important;
      page-break-inside: avoid;
    }
  }
`

export const PortalShell = styled.div`
  min-height: 100vh;
  background: ${t('color.bg', '#FAF7F2')};
  color: ${t('color.ink', '#1F2937')};
  font-family: ${t('type.family', "'Outfit', sans-serif")};
  display: flex;
  flex-direction: column;
`

export const PortalBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`

export const MainCanvas = styled.main`
  flex: 1;
  min-width: 0;
  padding: 96px 24px 96px;
  @media (min-width: 768px) {
    padding: 96px 40px 48px ${(props) => (props.$railOpen ? '288px' : '104px')};
  }
  @media (min-width: 1280px) {
    padding-left: ${(props) => (props.$railOpen ? '288px' : '104px')};
  }
`

export const ContentMaxWidth = styled.div`
  max-width: ${t('layout.contentMaxWidth', '1080px')};
  margin: 0 auto;
  width: 100%;
`

export const Card = styled.section`
  background: ${t('color.bgRaised', '#fff')};
  border: 1px solid ${t('color.border', '#E6E0D6')};
  border-radius: ${t('radius.lg', '20px')};
  padding: ${t('spacing.lg', '24px')};
  box-shadow: ${t('shadow.card', '0 1px 2px rgba(31,41,55,0.04)')};
`

export const CardTitle = styled.h2`
  font-size: ${t('type.title', '22px')};
  font-weight: 600;
  margin: 0 0 12px;
  color: ${t('color.ink', '#1F2937')};
`

export const Muted = styled.p`
  color: ${t('color.inkMuted', '#6B7280')};
  margin: 0;
  font-size: ${t('type.body', '15px')};
  line-height: 1.5;
`

export const PrimaryButton = styled.button`
  appearance: none;
  border: none;
  background: ${t('color.primary', '#1F3A5F')};
  color: white;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 22px;
  border-radius: ${t('radius.pill', '999px')};
  cursor: pointer;
  transition: background ${t('motion.standard', '200ms ease-out')};
  &:hover { background: ${t('color.primaryHover', '#15294A')}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

export const GhostButton = styled(PrimaryButton)`
  background: transparent;
  color: ${t('color.primary', '#1F3A5F')};
  border: 1px solid ${t('color.border', '#E6E0D6')};
  &:hover {
    background: ${t('color.primarySoft', '#E8EEF6')};
  }
`

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  background: ${(props) =>
    props.$tone === 'urgent'
      ? props.theme?.color?.urgentSoft || '#FBEAE9'
      : props.$tone === 'success'
      ? props.theme?.color?.accentSoft || '#E1ECE7'
      : props.theme?.color?.primarySoft || '#E8EEF6'};
  color: ${(props) =>
    props.$tone === 'urgent'
      ? props.theme?.color?.urgent || '#C24D49'
      : props.$tone === 'success'
      ? props.theme?.color?.accent || '#6B9080'
      : props.theme?.color?.primary || '#1F3A5F'};
`

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || props.theme?.spacing?.md || '16px'};
`

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.$gap || props.theme?.spacing?.md || '16px'};
  ${(props) =>
    props.$wrap &&
    css`
      flex-wrap: wrap;
    `};
  ${(props) =>
    props.$between &&
    css`
      justify-content: space-between;
    `};
`

export const PageHeading = styled.h1`
  font-size: ${t('type.titleXl', '34px')};
  margin: 0 0 8px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${t('color.ink', '#1F2937')};
`

export const PageSubheading = styled.p`
  margin: 0 0 32px;
  color: ${t('color.inkMuted', '#6B7280')};
  font-size: ${t('type.bodyLg', '17px')};
`

// In-app "back to parent" affordance. Critical for the PWA where browser
// back/forward chrome isn't visible — every tier-2 page renders one of these
// near the top so navigation up the hierarchy is a tap, not a swipe-from-edge.
export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 0;
  background: transparent;
  border: none;
  color: ${t('color.inkMuted', '#6B7280')};
  font-family: inherit;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: color ${t('motion.quick', '120ms ease-out')};
  &::before {
    content: '←';
    font-size: 16px;
    line-height: 1;
  }
  &:hover {
    color: ${t('color.primary', '#1F3A5F')};
  }
`
